import { faAdd, faArchive, faClone, faEdit, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, ListGroup, Row, Stack } from "react-bootstrap";
import { AssessmentType } from "../../../types/assessmenttype";
import Input from "../../ui/Input";
import { unsavedChanges } from "../../../App";
import {
  deleteAssessmentType,
  deleteAssessmentTypeQuestion,
  patchAssessmentType,
  patchAssessmentTypeQuestion,
  postAssessmentType,
  postAssessmentTypeQuestion,
  reorderAssessmentTypeQuestion,
} from "../../../services/httpClient";
import IconButton from "../../ui/IconButton";
import SelectObject from "../../ui/SelectObject";
import ReorderEditDeleteCards from "../../ui/ReorderEditDeleteCards";
import AssessmentCardQuestion from "./AssessmentCardQuestion";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";

interface AssessmentState {
  type: AssessmentType;
  setErrMessage: any;
  getAssessments: any;
  answerScales: any;
  showArchived: boolean;
  setShowArchived: any;
  onViewPreview: any;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  isDefault: boolean;
  isPublished: boolean;
}

const AssessmentCard = ({
  type,
  setErrMessage,
  getAssessments,
  answerScales,
  showArchived,
  setShowArchived,
  onViewPreview,
  setOrgToSave,
  setShowToast,
  setToastText,
  setToastBody,
  isDefault,
  isPublished,
}: AssessmentState) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [isUnpublishing, setIsUnpublishing] = useState<boolean>(false);
  const [isArchiving, setIsArchiving] = useState<boolean>(false);
  const [isUnarchiving, setIsUnarchiving] = useState<boolean>(false);
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [assessName, setAssessName] = useState<string>();
  const [frequency, setFrequency] = useState<number | null>();
  const [allowSave, setAllowSave] = useState<boolean>(false);
  const [currentQuestionName, setCurrentQuestionName] = useState<string>();
  const [questionFields, setQuestionFields] = useState<any[]>([]);
  const [updatedFields, setUpdatedFields] = useState<any[]>([]);
  const [showAnswerKeyValues, setShowAnswerKeyValues] = useState<boolean>(false);
  const [customEditId, setCustomEditId] = useState<string>();
  const [allowSaveQuestion, setAllowSaveQuestion] = useState<boolean>(false);
  const [showAddQuestion, setShowAddQuestion] = useState<boolean>(false);
  const [enableAddQuestion, setEnableAddQuestion] = useState<boolean>(false);
  const [newQuestion, setNewQuestion] = useState<string>("");

  const defaultAnswerScaleId = answerScales.find((s: any) => s.id === type?.question[0].field_set_id)?.id;
  const defaultAnswerScaleFields = answerScales.find((s: any) => s.id === type?.question[0].field_set_id)?.fields;

  const frequencyOptions = [
    { key: 7, name: "Weekly" },
    { key: 14, name: "Every 2 Weeks" },
    { key: 30, name: "Monthly" },
    { key: 90, name: "Every 3 Months" },
    { key: 180, name: "Every 6 Months" },
    { key: 365, name: "Yearly" },
  ];

  useEffect(() => {
    setAssessName(type?.name);
    setFrequency(type?.frequency);
  }, [type]);

  const onClickShowAddQuestion = () => {
    setShowAddQuestion(true);
    setQuestionFields(defaultAnswerScaleFields);
  };

  const onClickCancelAddQuestion = () => {
    setShowAddQuestion(false);
    setShowAnswerKeyValues(false);
    setEnableAddQuestion(false);
    setNewQuestion("");
  };

  const updateNewQuestion = (e: any) => {
    setNewQuestion(e.target.value);
    setEnableAddQuestion(true);
  };

  const onClickAddQuestion = (e: any) => {
    e.preventDefault();

    const newAssessmentQuestion = {
      question: newQuestion,
      key: type?.question.length + 1,
      order: type?.question.length + 1,
      field_set_id: defaultAnswerScaleId,
      fields:
        updatedFields.length > 0
          ? updatedFields.map((f: any) => ({
              id: f.field_id,
              value_description: f.value_description,
            }))
          : null,
    };

    postAssessmentTypeQuestion(newAssessmentQuestion, type?.id)
      .then((res: any) => {
        setShowAddQuestion(false);
        setNewQuestion("");
        setOrgToSave(false);
        getAssessments(false);
        setShowAnswerKeyValues(false);
        setShowToast(true);
        setToastText("Assessment Question Added");
        setToastBody(newQuestion);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const onClickSave = (showPublished: boolean) => {
    var updatedAssessment: any;
    if (isDefault) {
      updatedAssessment = {
        name: assessName,
        frequency: frequency,
        is_published: type?.is_published,
        assessmenttype_id: type?.id,
      };
    } else {
      updatedAssessment = {
        name: assessName,
        frequency: frequency,
      };
    }

    setErrMessage("");

    patchAssessmentType(updatedAssessment, type?.id)
      .then((res: any) => {
        setIsEditing(false);
        setAllowSave(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getAssessments(showPublished);
        setShowToast(true);
        setToastText("Assessment Updated");
        setToastBody(assessName);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickCancel = () => {
    setErrMessage("");
    setAssessName(type?.name);
    setAllowSave(false);
    setIsEditing(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onClickAssessSubmit = (e: any) => {
    e?.preventDefault();
    onClickSave(false);
  };

  const updateAssessName = (e: any) => {
    setAssessName(e.target.value);
    setAllowSave(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateFrequency = (e: any) => {
    setFrequency(e.target.value);
    setAllowSave(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickPublish = () => {
    setIsPublishing(true);
    setShowAddQuestion(false);
  };

  const onClickUnpublish = () => {
    setIsUnpublishing(true);
  };

  const onClickCancelUnpublish = () => {
    setIsUnpublishing(false);
  };

  const onClickConfirmUnpublish = () => {
    const updatedAssessment = {
      name: assessName,
      frequency: frequency,
      is_published: 0,
      assessmenttype_id: type?.id,
    };

    patchAssessmentType(updatedAssessment, type?.id)
      .then((res: any) => {
        setIsEditing(false);
        setIsUnpublishing(false);
        setAllowSave(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getAssessments(true);
        setShowToast(true);
        setToastText("Assessment Updated");
        setToastBody(assessName);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickCancelPublish = () => {
    setIsPublishing(false);
  };

  const onClickConfirmPublish = (org_id: string | null) => {
    var publishAssessment;
    if (org_id) {
      //custom
      publishAssessment = {
        is_published: true,
      };
    } else {
      //default
      publishAssessment = {
        assessmenttype_id: type?.id,
        frequency: frequency,
        is_published: true,
      };
    }

    setErrMessage("");

    patchAssessmentType(publishAssessment, type?.id)
      .then((res: any) => {
        setIsPublishing(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "publish_custom_assessment");
        getAssessments(true);
        setShowToast(true);
        setToastText("Assessment Published");
        setToastBody(assessName);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickArchive = () => {
    setIsArchiving(true);
  };

  const onClickCancelArchive = () => {
    setIsArchiving(false);
  };

  const onClickConfirmArchive = () => {
    const archiveAssessment = {
      is_archived: true,
    };

    setErrMessage("");

    patchAssessmentType(archiveAssessment, type?.id)
      .then((res: any) => {
        setIsArchiving(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "archive_custom_assessment");
        getAssessments(true);
        setShowToast(true);
        setToastText("Assessment Archived");
        setToastBody(type?.name);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickUnarchive = () => {
    setIsUnarchiving(true);
  };

  const onClickCancelUnarchive = () => {
    setIsUnarchiving(false);
  };

  const onClickConfirmUnarchive = () => {
    const unarchiveAssessment = {
      is_archived: false,
    };

    setErrMessage("");

    patchAssessmentType(unarchiveAssessment, type?.id)
      .then((res: any) => {
        setIsUnarchiving(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "unarchive_custom_assessment");
        setErrMessage("");
        setShowArchived(false);
        getAssessments(true);
        setShowToast(true);
        setToastText("Assessment Published");
        setToastBody(type?.name);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickDuplicate = () => {
    setIsDuplicating(true);
  };

  const onClickCancelDuplicate = () => {
    setIsDuplicating(false);
  };

  const onClickConfirmDuplicate = () => {
    const duplicateAssessment = {
      name: type?.name + " - Copy",
      frequency: type?.frequency,
      questions: type?.question.map((q: any, index: number) => ({
        key: q?.key,
        question: q?.question,
        field_set_id: q?.field_set_id,
        fields: q?.fields
          ?.filter((f: any) => f?.value != f?.value_description)
          .map((f: any) => ({
            field_id: f?.id,
            value_description: f?.value_description,
          })),
        order: index + 1,
      })),
    };

    setErrMessage("");

    postAssessmentType(duplicateAssessment)
      .then((res: any) => {
        setIsDuplicating(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "duplicate_custom_assessment");
        setErrMessage("");
        setShowArchived(false);
        getAssessments(false);
        setShowToast(true);
        setToastText("Assessment Duplicated");
        setToastBody(type?.name);
      })
      .catch((err: any) => {
        setIsDuplicating(false);
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
  };

  const onClickConfirmDelete = () => {
    setErrMessage("");
    deleteAssessmentType(type?.id)
      .then((res: any) => {
        setIsDeleting(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        // trackFeatureEvent("button_click", "delete_custom_assessment");
        getAssessments(false);
        setShowToast(true);
        setToastText("Assessment Deleted");
        setToastBody(type?.name);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickEditQuestionFinal = (questionId: string) => {
    var updatedData: any = {};
    if (currentQuestionName) updatedData = { ...updatedData, question: currentQuestionName };
    if (updatedFields.length > 0) updatedData = { ...updatedData, fields: updatedFields };

    setErrMessage("");
    patchAssessmentTypeQuestion(updatedData, type?.id, questionId)
      .then((res: any) => {
        setAllowSaveQuestion(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        setCustomEditId("");
        setCurrentQuestionName("");
        setShowAnswerKeyValues(false);
        getAssessments();
        setShowToast(true);
        setToastText("Assessment Question Updated");
        setToastBody("");
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickQuestionSubmit = (questionId: string) => {
    onClickEditQuestionFinal(questionId);
  };

  const updateAnswerKey = (e: any, index: number) => {
    //this is managing the data to be sent to the backend
    const questionField = questionFields.find((item) => item.value === index);

    if (questionField) {
      const updatedField = { ...questionField, value_description: e.target.value };

      setUpdatedFields((prev) => {
        const index = updatedFields.findIndex((item) => item.value === updatedField.value);
        if (index !== -1) {
          // Update existing entry
          return updatedFields.map((item, i) => (i === index ? updatedField : item));
        } else {
          // Add new entry
          return [...prev, updatedField];
        }
      });
    }

    //this is solely for updating the UI
    setQuestionFields((prev) => prev.map((item) => (item.value === index ? { ...item, value_description: e.target.value } : item)));
    setAllowSaveQuestion(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickEditQuestionCancel = () => {
    setCustomEditId("");
    setCurrentQuestionName("");
    setUpdatedFields([]);
    setShowAnswerKeyValues(false);
    setAllowSaveQuestion(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onClickDeleteQuestionFinal = (questionId: string, questionName: string) => {
    deleteAssessmentTypeQuestion(type?.id, questionId).then((res: any) => {
      setAllowSaveQuestion(false);
      unsavedChanges.value = false;
      setOrgToSave(false);
      setShowToast(true);
      setToastText("Question Deleted");
      setToastBody(questionName);
      getAssessments();
    });
  };

  const updateQuestionOrder = (newOrder: any) => {
    reorderAssessmentTypeQuestion(newOrder, type?.id)
      .then(() => {
        setShowToast(true);
        setToastText("Question Order");
        setToastBody(type?.name);
        getAssessments();
      })
      .catch((err) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const renderPublish = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to publish - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmPublish(type?.organization_id)}>
              Confirm Publish
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelPublish()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderUnpublish = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>
                Are you sure you want to {isDefault ? "archive" : "unpublish"} - {type?.name}?
              </Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmUnpublish()}>
              Confirm {isDefault ? "Archive" : "Unpublish"}
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelUnpublish()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderArchive = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to archive - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmArchive()}>
              Confirm Archive
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelArchive()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderUnarchive = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to publish and remove from archive - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmUnarchive()}>
              Confirm Publish
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelUnarchive()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderDelete = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to delete - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickConfirmDelete()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderDuplicate = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to create duplicate copy in drafts - {type?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-tertiary-color button" onClick={() => onClickConfirmDuplicate()}>
              Confirm Duplicate
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDuplicate()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const updateQuestion = (e: any) => {
    setCurrentQuestionName(e.target.value);
    setAllowSaveQuestion(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const customEditQuestionLevel = (questionId: any) => {
    var currentQuestion = type?.question?.find((q: any) => q.id === questionId);
    const questionNumber = (currentQuestion.key as number) - 1;
    if (updatedFields.length === 0) {
      setQuestionFields(currentQuestion?.fields);
    }

    return (
      <>
        <Form onSubmit={() => onClickQuestionSubmit(questionId)} className="mt-2">
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Input
                label="Question"
                type="text"
                value={currentQuestionName ? currentQuestionName : currentQuestion?.question}
                onUpdate={(e: any) => updateQuestion(e)}
              />
            </Stack>
            <Button
              className="button app-secondary-color"
              onClick={() => onClickEditQuestionFinal(questionId)}
              disabled={!allowSaveQuestion}
            >
              Save
            </Button>
            <div className="vr" />
            <Button className="button app-primary-color" onClick={(e: any) => onClickEditQuestionCancel()}>
              Cancel
            </Button>
          </Stack>
        </Form>
        {!showAnswerKeyValues ? (
          <h6
            className="app-tertiary-color-inverse cursor-pointer mt-2 mb-2 ms-1 bg-light"
            onClick={() => setShowAnswerKeyValues(!showAnswerKeyValues)}
          >
            {"Edit Answer Key Descriptions"}
          </h6>
        ) : (
          <h6 className="mt-3 ms-1 me-3">Answer Key Descriptions</h6>
        )}

        {showAnswerKeyValues && (
          <Stack style={{ justifyContent: "center" }} className="col-12 mb-5" gap={2}>
            {questionFields.length > 0
              ? questionFields?.map((field: any, index: number) => (
                  <Stack direction="horizontal" gap={2} className="col-8">
                    <Col>
                      <Input
                        label="Answer Key Value"
                        type="text"
                        value={field?.value_description}
                        onUpdate={(e: any) => updateAnswerKey(e, index)}
                      />
                    </Col>
                  </Stack>
                ))
              : currentQuestion.fields?.map((field: any, index: number) => (
                  <Stack direction="horizontal" gap={2} className="col-8">
                    <Col>
                      <Input
                        label="Answer Key Value"
                        type="text"
                        value={field?.value_description}
                        onUpdate={(e: any) => updateAnswerKey(e, index)}
                      />
                    </Col>
                  </Stack>
                ))}
          </Stack>
        )}
      </>
    );
  };

  const handleEdit = (id: any) => {
    setCustomEditId(id);
  };

  return (
    <>
      {isPublishing ? (
        renderPublish()
      ) : isUnpublishing ? (
        renderUnpublish()
      ) : isArchiving ? (
        renderArchive()
      ) : isUnarchiving ? (
        renderUnarchive()
      ) : isDuplicating ? (
        renderDuplicate()
      ) : isDeleting ? (
        renderDelete()
      ) : (
        <Card className="bg-light p-3" key={type?.id}>
          {isEditing ? (
            <Form onSubmit={(e: any) => onClickAssessSubmit(e)}>
              <Stack direction="horizontal" gap={3} className="mb-3">
                <Stack style={{ justifyContent: "center" }} className="col-6">
                  <Input
                    label="Assessment Name"
                    disabled={type?.is_published ? true : false}
                    type="textarea"
                    value={assessName}
                    onUpdate={(e: any) => updateAssessName(e)}
                  />
                </Stack>
                <SelectObject label="Frequency" value={frequency} options={frequencyOptions} onUpdate={(e: any) => updateFrequency(e)} />
                <Button
                  className="button app-secondary-color button"
                  onClick={() => onClickSave(type?.is_published ? true : false)}
                  disabled={!allowSave}
                >
                  Save
                </Button>
                <div className="vr" />
                <Button className="button app-primary-color button" onClick={() => onClickCancel()}>
                  Cancel
                </Button>
              </Stack>
            </Form>
          ) : (
            <Stack direction="horizontal" gap={3}>
              <Stack style={{ justifyContent: "center" }} className="col-6">
                <h5 className="app-orange-text">{type.name}</h5>
              </Stack>
              {type?.is_archived ? (
                <Badge bg="secondary">{type?.frequency ? type?.frequency + " Days" : "As Needed"}</Badge>
              ) : (
                <Badge className={isDefault ? "cursor-pointer" : "cursor-pointer"} bg="secondary" onClick={() => onClickEdit()}>
                  {type?.frequency ? type?.frequency + " Days" : "As Needed"}
                </Badge>
              )}
              {!isDefault && (
                <>
                  {type?.is_archived ? (
                    <>
                      <IconButton onClickButton={() => onViewPreview(type)} tooltipText="Preview" icon={faEye} />
                    </>
                  ) : type?.is_published ? (
                    <>
                      <IconButton onClickButton={() => onViewPreview(type)} tooltipText="Preview" icon={faEye} />
                      <IconButton onClickButton={() => onClickArchive()} tooltipText="Archive" icon={faArchive} />
                    </>
                  ) : (
                    <>
                      <IconButton onClickButton={() => onViewPreview(type)} tooltipText="Preview" icon={faEye} />
                      <IconButton onClickButton={() => onClickEdit()} tooltipText="Edit Name" icon={faEdit} />
                      <IconButton onClickButton={() => onClickDelete()} tooltipText="Delete" icon={faTrash} />
                    </>
                  )}
                  <div>
                    <IconButton onClickButton={() => onClickDuplicate()} tooltipText="Duplicate" icon={faClone} />
                  </div>
                </>
              )}
            </Stack>
          )}
          {!isDefault && (
            <>
              <Row>
                <h6 className="text-muted text-capitalize">{"Answer Scale: " + type?.question[0]?.fields[0]?.name} </h6>
              </Row>
              <Row>
                <h6>Questions</h6>
              </Row>
            </>
          )}
          {!isDefault && (
            <Stack gap={2}>
              {isPublished || showArchived
                ? type?.question.map((t: any) => <AssessmentCardQuestion question={t} />)
                : type?.question?.length > 0 && (
                    <ReorderEditDeleteCards
                      items={type?.question}
                      onDeleteFinal={onClickDeleteQuestionFinal}
                      updateOrder={updateQuestionOrder}
                      customRenderEdit={customEditQuestionLevel}
                      customEditId={customEditId}
                      handleEdit={handleEdit}
                      setErrMessage={setErrMessage}
                    />
                  )}
            </Stack>
          )}
          {showAddQuestion && (
            <>
              <Form onSubmit={(e: any) => onClickAddQuestion(e)} className="mt-3">
                <Stack direction="horizontal" gap={3}>
                  <Stack style={{ justifyContent: "center" }} className="col-6">
                    <Input label="New Question" type="text" value={newQuestion} onUpdate={(e: any) => updateNewQuestion(e)} />
                  </Stack>
                  <Button type="submit" className="app-secondary-color button" disabled={!enableAddQuestion}>
                    Add
                  </Button>
                  <div className="vr" />
                  <Button className="app-primary-color button" onClick={() => onClickCancelAddQuestion()}>
                    Cancel
                  </Button>
                </Stack>
              </Form>
              {!showAnswerKeyValues ? (
                <h6
                  className="app-tertiary-color-inverse cursor-pointer mt-2 mb-2 bg-light"
                  onClick={() => setShowAnswerKeyValues(!showAnswerKeyValues)}
                >
                  {"Edit Answer Key Descriptions"}
                </h6>
              ) : (
                <h6 className="mt-3 ms-1">Answer Key Descriptions</h6>
              )}
              {showAnswerKeyValues && (
                <Stack style={{ justifyContent: "center" }} className="col-12" gap={2}>
                  {questionFields.length > 0 &&
                    questionFields?.map((field: any, index: number) => (
                      <Stack direction="horizontal" gap={2} className="col-8">
                        <Col>
                          <Input
                            label="Answer Key Value"
                            type="text"
                            value={field?.value_description}
                            onUpdate={(e: any) => updateAnswerKey(e, index)}
                          />
                        </Col>
                      </Stack>
                    ))}
                </Stack>
              )}
            </>
          )}
          {type?.is_archived ? (
            <Row className="mt-3">
              <Col className="text-end">
                <Button className="button app-tertiary-color" onClick={() => onClickUnarchive()}>
                  Publish
                </Button>
              </Col>
            </Row>
          ) : !type?.is_published ? (
            <Row className="mt-3">
              {!isDefault && (
                <Col className="text-start align-content-end">
                  <IconEmbeddedButton onClickButton={() => onClickShowAddQuestion()} tooltipText="Add Question" icon={faAdd} />
                </Col>
              )}
              <Col xs={isDefault ? 12 : 2} className={isDefault ? "text-center" : "text-end"}>
                <Button className="button app-tertiary-color" onClick={() => onClickPublish()}>
                  Publish
                </Button>
              </Col>
            </Row>
          ) : (
            isDefault && (
              <Row>
                <Col xs={12} className="text-center">
                  <Button className="button app-primary-color" onClick={() => onClickUnpublish()}>
                    Archive
                  </Button>
                </Col>
              </Row>
            )
          )}
        </Card>
      )}
    </>
  );
};

export default AssessmentCard;
